import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, alpha } from '@mui/material/styles';
import { StyledTableCell, StyledTableRow, ToolTipStyle } from './styles';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import useStyles from './styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  Skeleton,
  Button,
  Fade,
  Chip,
  Collapse
} from '@mui/material';
import { JobStatus, PermissionChips } from 'components/common';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';


export default function TableCustom({
  columns=null,
  childrenColumns=null,
  content=[],
  title,
  loading,
  handleRefresh,
  handleNotResults=null,
  handleOnClickRow=null,
  config={},
  actions=null,
  childrenActions=null
}) {
  const [t] = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  const defaultConfig = {
    maxHeight: config?.maxHeight || 'inherit',
    minHeight: config?.minHeight || 'inherit',
    size: config?.size || 'medium',
    notResults: {
      icon: config?.notResults?.icon || SearchOffOutlinedIcon,
      size: config?.notResults?.size || '50vh',
      disabledIcon: config?.notResults?.disabledIcon || false,
      title:
        config?.notResults?.title || 'common.components.table.titleNotResults',
      description:
        config?.notResults?.description ||
        'common.components.table.descriptionNotResults',
      buttonLabel:
        config?.notResults?.buttonLabel || 'common.components.table.buttonNew',
    },
  };

  const setHandleOnClickRow = (item) => {
    if(handleOnClickRow) return () => handleOnClickRow(item);
  }


  function Cell({ item, col }) {
    return (
      <>
        { col?.render ?
          (
            col.render(item)
          )
        : col?.type === 'chip' ?
          (
            <Chip label={ t(item[col.key]) } color='primary'/>
          )
          : col?.type === 'job' ?
          (
            <>
              { item[col.key][0] &&
                <>
                  { item[col.key].map((job, index) => (
                    <JobStatus sx={{ mr: 0.5 }} key={index} status={job.status} job={job} />
                  ))}
                </>
              }
            </>
          )
          : col?.type === 'status' ?
          (
            <JobStatus
              sx={{ mr: 0.5 }}
              status={item[col.key]}
              jobId={item[col.key]}
            />
          )
          : col?.type === 'permissions' ?
          (
            <PermissionChips
              view    = { item[col.key].view }
              content = { item[col.key] }
            />
          )
          :
          (
            <>{ (item[col.key]) }</>
          )
        }
      </>
    );
  }

  function ChildrenRow({ open, children }) {
    return (
      <StyledTableRow>
        <StyledTableCell
          size={defaultConfig.size}
          sx={{
            padding: 0,
            backgroundColor: 'white'}}
          colSpan={columns.length + 1}
        >
          <Collapse
            in={open}
            timeout="auto"
            unmountOnExit
            sx={{
              paddingTop: 2, paddingLeft: 2, marginLeft: 3,
              borderLeft: `solid 2px ${theme.palette.primary[100]}`
             }}
          >
            <Table aria-label="customized table children" stickyHeader>
              <TableBody >
                { children.map((item, index) => (
                  <Row
                    key={index}
                    item={item}
                    columns={childrenColumns}
                    actions={childrenActions}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </StyledTableCell>
      </StyledTableRow>
    );
  }

  function Row({ item, columns, actions }) {
    const children = item?.children || null;
    const [open, setOpen] = useState(false);
    const rowActions = typeof actions === 'function' ? actions(item) : actions;

    return (
      <>
        <StyledTableRow
          sx={ handleOnClickRow && {
            '&:hover': {
              transition: '.3s',
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 150%)`,
              cursor: 'pointer',
              borderRadius: theme.spacing(6)
            },
            '&:hover > td, &:hover > th': {
              color: theme.palette.common.white
            }
          }}
        >
          { columns.map((col, index) => (
            <StyledTableCell
              onClick={setHandleOnClickRow(item)}
              key={index}
              colSpan={col?.colSpan ? item[col.colSpan] : 1}
              size={defaultConfig.size}
              component="th"
              scope="row"
              sx={ col?.sx ? col.sx : {} }
            >
              <Cell
                item={item}
                col={col}
              />
            </StyledTableCell>
          ))}
          { rowActions &&
            <StyledTableCell
              sx={{ p: 0 }} size={defaultConfig.size}
              key={'action-row-col'}
              component="th"
              scope="row"
              align="right"
            >
              <Box
                component='div'
                sx={{
                mr: 1
              }}>
                { rowActions.map((action, index) => (
                  
                  (action?.stop && !(['RU','WA'].includes(item?.status)))
                    ? null :
                  
                  <ToolTipStyle
                    arrow
                    key={index}
                    disableFocusListener
                    placement="top-start"
                    title={action.label}
                    TransitionComponent={Fade}
                    TransitionProps={{ timeout: 300 }}
                    enterDelay={700}
                  >
                    { (action.handle === 'children' && children && children.length > 0) ?
                      (
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => setOpen(!open)}
                        >
                          { open ?
                            <ExpandCircleDownOutlinedIcon sx={{ transform: 'rotateZ(180deg)'}} color='primary' />
                            : <ExpandCircleDownOutlinedIcon color='primary' />
                          }
                        </IconButton>
                      )
                      :(
                        <IconButton
                          onClick={() => action.handle(item, index)}
                          aria-label={`action-${index}`}
                          size="small"
                        >
                          {action.icon}
                        </IconButton>
                      )
                    }
                  </ToolTipStyle>
                ))}
              </Box>
            </StyledTableCell>
          }
        </StyledTableRow>

        { (children && children.length > 0) &&
          <ChildrenRow open={open} children={children} />
        }
      </>
    );
  }

  return (
    <>
      { loading ? (
        <Skeleton height="50vh" variant="rounded" animation="wave" />
      ) : (
        <>
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              <Typography variant="h6" id="titleList">
                {t(title)}
              </Typography>
              { handleRefresh && (
                <IconButton
                  aria-label="refresh"
                  size="small"
                  onClick={handleRefresh}
                >
                  <RefreshIcon
                    fontSize="medium"
                    className={classes.iconRefresh}
                  />
                </IconButton>
              )}
            </Box>
          </Box>
          { (content?.length === 0) ? (
            <Box className={classes.notResults} sx={{ maxHeight: defaultConfig.maxHeight, height: defaultConfig.notResults.size  }}>
              <Box
                className={classes.notResultsDraw}
                sx={{
                  height: config?.size === 'medium' ? '12rem' : '10rem',
                  width: config?.size === 'medium' ? '12rem' : '10rem',
                  display: defaultConfig.notResults.disabledIcon ? 'none' : 'flex'
                }}
              >
                <defaultConfig.notResults.icon
                  className={classes.notResultsDrawIcon}
                  sx={{
                    fontSize: config?.size === 'medium' ? '9rem' : '7rem',
                  }}
                />
              </Box>
              <Typography variant="h5" id="titleList">
                {t(defaultConfig.notResults.title)}
              </Typography>
              { config?.size === 'medium' &&
                <>
                  <Typography variant="subtitle1" id="titleList" color="gray">
                    {t(defaultConfig.notResults.description)}
                  </Typography>
                  { handleNotResults && (
                    <Button onClick={handleNotResults} variant="text" size="large">
                      {t(defaultConfig.notResults.buttonLabel)}
                    </Button>
                  )}
                </>
              }
            </Box>
          ) : content && (
            <TableContainer
              component="div"
              id="table"
              aria-label="table"
              sx={{ maxHeight: defaultConfig.maxHeight, minHeight: defaultConfig.minHeight }}
              className={classes.scrollbar}
            >
              <Table aria-label="customized table" stickyHeader>
                <TableHead>
                  <TableRow>
                    { columns.map((col, index) => (
                      <StyledTableCell size={defaultConfig.size} key={index}>{col.label}</StyledTableCell>
                    ))}
                    { actions &&
                      <StyledTableCell size={defaultConfig.size}>{''}</StyledTableCell>
                    }
                  </TableRow>
                </TableHead>
                <TableBody >
                  { content.map((item, index) => (
                    <Row
                      key={index}
                      item={item}
                      columns={columns}
                      actions={actions}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </>
  );
}
